import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Users, Plus, Trash2, Shield, User, X, Power, PowerOff, RefreshCw, ShieldCheck, ShieldOff } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api/admin';

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
});

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'admin'
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchCurrentUser();
  }, []);
  
  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/me`, getAuthHeader());
      if (response.data?.email) {
        setCurrentUserEmail(response.data.email);
      }
    } catch (err) {
      console.error('Error fetching current user:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, getAuthHeader());
      setUsers(response.data.admins || []);
    } catch (err) {
      setError('Failed to load admin users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await axios.post(`${API}/users`, formData, getAuthHeader());
      setSuccess(`Admin user '${formData.username}' created successfully`);
      setFormData({ username: '', email: '', password: '', role: 'admin' });
      setShowAddForm(false);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create admin user');
    }
  };

  const handleDelete = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to delete admin user '${username}'?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/users/${userId}`, getAuthHeader());
      setSuccess(`Admin user '${username}' deleted successfully`);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete admin user');
    }
  };

  const handleEnableAccount = async (userId, username) => {
    setActionLoading(userId);
    try {
      await axios.post(`${API}/users/${userId}/enable`, {}, getAuthHeader());
      toast.success(`Account '${username}' has been enabled`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to enable account');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDisableAccount = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to disable '${username}'? They will not be able to log in.`)) {
      return;
    }
    setActionLoading(userId);
    try {
      await axios.post(`${API}/users/${userId}/disable`, { reason: 'Disabled by admin' }, getAuthHeader());
      toast.success(`Account '${username}' has been disabled`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disable account');
    } finally {
      setActionLoading(null);
    }
  };

  const handleForceReset = async (userId, username) => {
    if (!window.confirm(`Force password reset for '${username}'? This will also reset their 2FA.`)) {
      return;
    }
    setActionLoading(userId);
    try {
      const response = await axios.post(`${API}/users/${userId}/force-reset`, {}, getAuthHeader());
      toast.success(response.data.message || `Password reset required for '${username}'`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to force reset');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#5a6f4e] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#c5bfa9]">Admin Users</h1>
          <p className="text-gray-400 mt-1">Manage admin accounts and permissions</p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-2 px-4 py-2 bg-[#3d4a35] hover:bg-[#4a5d42] text-white rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Admin User
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-center justify-between bg-red-900/30 border border-red-700/50 text-red-400 px-4 py-3 rounded-lg">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-400 hover:text-red-300">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {success && (
        <div className="flex items-center justify-between bg-green-900/30 border border-green-700/50 text-green-400 px-4 py-3 rounded-lg">
          <span>{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-400 hover:text-green-300">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Add User Form */}
      {showAddForm && (
        <Card className="bg-[#1e2419] border-[#3d4a35]">
          <CardHeader>
            <CardTitle className="text-[#c5bfa9]">Create New Admin User</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Username</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder="Enter username"
                    required
                    minLength="3"
                    className="w-full px-3 py-2 border border-[#3d4a35] rounded-lg bg-[#252b1e] text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-[#5a6f4e] focus:border-[#5a6f4e]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Enter email"
                    required
                    className="w-full px-3 py-2 border border-[#3d4a35] rounded-lg bg-[#252b1e] text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-[#5a6f4e] focus:border-[#5a6f4e]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Password</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Enter password"
                    required
                    minLength="8"
                    className="w-full px-3 py-2 border border-[#3d4a35] rounded-lg bg-[#252b1e] text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-[#5a6f4e] focus:border-[#5a6f4e]"
                  />
                  <p className="text-xs text-gray-500 mt-1">Minimum 8 characters. User will be forced to change on first login.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Role</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full px-3 py-2 border border-[#3d4a35] rounded-lg bg-[#252b1e] text-gray-100 focus:ring-2 focus:ring-[#5a6f4e] focus:border-[#5a6f4e]"
                  >
                    <option value="marshal">Marshal (Check-in only)</option>
                    <option value="admin">Admin</option>
                    <option value="superadmin">Superadmin</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Create Admin User
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="px-4 py-2 border border-[#3d4a35] bg-transparent text-gray-300 hover:bg-[#252b1e] rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Users List */}
      <div className="space-y-3 max-w-full">
        <h2 className="text-lg font-semibold text-[#c5bfa9]">Current Admin Users</h2>
        {users.map((user) => {
          const isCurrentUser = user.email === currentUserEmail;
          
          return (
            <Card key={user.id} className={`bg-[#1e2419] border-[#3d4a35] overflow-hidden w-full ${user.is_active === false ? 'opacity-60' : ''}`}>
              <CardContent className="py-4 px-4">
                <div className="flex flex-col gap-3">
                  <div className="flex items-start gap-3">
                    <div className={`flex-shrink-0 p-2 rounded-lg ${
                      user.role === 'superadmin' ? 'bg-purple-900/40' :
                      user.role === 'marshal' ? 'bg-blue-900/40' : 'bg-green-900/40'
                    }`}>
                      {user.role === 'superadmin' ? (
                        <Shield className="w-5 h-5 text-purple-400" />
                      ) : (
                        <User className={`w-5 h-5 ${user.role === 'marshal' ? 'text-blue-400' : 'text-green-400'}`} />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-gray-100">{user.username}</h3>
                        {isCurrentUser && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded bg-emerald-600 text-white">You</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-400 truncate">{user.email} • Role: {user.role === 'marshal' ? 'Marshal' : user.role === 'superadmin' ? 'Super Admin' : 'Admin'}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Created: {new Date(user.created_at).toLocaleDateString('en-GB')} • Last login: {user.last_login ? new Date(user.last_login).toLocaleString('en-GB') : 'Never'}
                      </p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {user.is_active === false && (
                          <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-red-900/50 text-red-400">
                            Disabled
                          </span>
                        )}
                        {user.totp_enabled && (
                          <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-blue-900/50 text-blue-400" title="2FA Enabled">
                            <ShieldCheck className="w-3 h-3 inline mr-1" />2FA
                          </span>
                        )}
                        {user.force_password_change && (
                          <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-orange-900/50 text-orange-400">
                            Reset Required
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  {!isCurrentUser && (
                    <div className="flex flex-wrap gap-2 mt-2 pl-11">
                      {user.is_active === false ? (
                        <button
                          onClick={() => handleEnableAccount(user.id, user.username)}
                          disabled={actionLoading === user.id}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs bg-green-900/40 text-green-400 hover:bg-green-900/60 border border-green-700/50 rounded transition-colors disabled:opacity-50"
                        >
                          <Power className="w-3 h-3" />
                          Enable
                        </button>
                      ) : (
                        <button
                          onClick={() => handleDisableAccount(user.id, user.username)}
                          disabled={actionLoading === user.id}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs bg-yellow-900/40 text-yellow-400 hover:bg-yellow-900/60 border border-yellow-700/50 rounded transition-colors disabled:opacity-50"
                        >
                          <PowerOff className="w-3 h-3" />
                          Disable
                        </button>
                      )}
                      <button
                        onClick={() => handleForceReset(user.id, user.username)}
                        disabled={actionLoading === user.id}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-900/40 text-blue-400 hover:bg-blue-900/60 border border-blue-700/50 rounded transition-colors disabled:opacity-50"
                      >
                        <RefreshCw className="w-3 h-3" />
                        Force Reset
                      </button>
                      <button
                        onClick={() => handleDelete(user.id, user.username)}
                        disabled={actionLoading === user.id}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs bg-red-900/40 text-red-400 hover:bg-red-900/60 border border-red-700/50 rounded transition-colors disabled:opacity-50"
                      >
                        <Trash2 className="w-3 h-3" />
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Empty State */}
      {users.length === 0 && (
        <Card className="bg-[#1e2419] border-[#3d4a35]">
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 mx-auto text-gray-600 mb-4" />
            <h3 className="text-lg font-medium text-gray-200 mb-2">No Admin Users</h3>
            <p className="text-gray-500 mb-4">Get started by creating your first admin user</p>
            <button
              onClick={() => setShowAddForm(true)}
              className="px-4 py-2 bg-[#3d4a35] hover:bg-[#4a5d42] text-white rounded-lg transition-colors"
            >
              Add Admin User
            </button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminUsers;
